import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { IScanReportResponse } from '../../../../../../../shared/interfaces/async-report.interface';
import { IScan } from '../../../../../../../shared/interfaces/scan.interface';
import { AccessibilityAuditToolNames } from '../../../../../../../shared/constants/audit-tool';
import { FileExtension } from '../../../../../../../shared/constants/file-extension';
import { ReportService } from '../../../../../services/report.service';
import { EmulatorScanReportService } from '../../../../../services/emulator-scan-report.service';
import { RequiredSecurities } from '../../../../../../../shared/constants/required-securities';
import { $scan } from '../../../../../../../shared/constants/scan';
import { IRequiredSecurity } from '../../../../../../../shared/interfaces/security.interface';
import { MobileEngineScanOrigins, ScanOrigin } from '../../../../../../../shared/constants/scanning';

@Injectable()
export class ScanReportHandlerProvider {
  constructor(
    private reportService: ReportService,
    private emulatorScanReportService: EmulatorScanReportService,
  ) {}

  get(scan: IScan): ScanReportHandler {
    if (scan[$scan.scanOrigin] === ScanOrigin.mobile_emulator) {
      return new MobileEmulatorScanReportHandler(this.emulatorScanReportService, scan);
    }

    if (scan[$scan.scanOrigin] === ScanOrigin.mobile_xml) {
      return new MobileXmlScanReportHandler(this.reportService, scan);
    }

    if (MobileEngineScanOrigins.includes(scan[$scan.scanOrigin])) {
      return new MobileODTScanReportHandler(this.reportService, scan);
    }

    // mobile-sdk still use an existing web scan end-point
    return new DefaultScanReportHandler(this.reportService, scan);
  }
}

export abstract class ScanReportHandler {
  abstract generateFullScanReport(
    auditTool: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
    scanUrl: string,
  ): Observable<IScanReportResponse>;

  abstract getFullScanReport(
    auditTool: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
    scanUrl: string,
  ): Observable<IScanReportResponse>;

  abstract sendReportToEmails(emails: string[], auditTool: AccessibilityAuditToolNames | null): Observable<string[]>;

  /**
   * Returns the required security for UI visibility control.
   * This is specifically used to hide UI elements when the user doesn't have mobile permissions.
   */
  abstract getRequiredSecurityForDownload(): IRequiredSecurity;
  abstract getRequiredSecurityForReportShare(): IRequiredSecurity;
}

class MobileEmulatorScanReportHandler extends ScanReportHandler {
  constructor(
    private emulatorScanReportService: EmulatorScanReportService,
    private scan: IScan,
  ) {
    super();
  }

  generateFullScanReport(
    auditTool: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
    scanUrl: string,
  ): Observable<IScanReportResponse> {
    return this.emulatorScanReportService.generateFullScanReport(this.scan[$scan._id], fileExtension, scanUrl);
  }

  getFullScanReport(
    auditTool: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
    scanUrl: string,
  ): Observable<IScanReportResponse> {
    return this.emulatorScanReportService.getFullScanReport(this.scan[$scan._id], fileExtension, scanUrl);
  }

  sendReportToEmails(emails: string[]): Observable<string[]> {
    return this.emulatorScanReportService.sendScanReportToEmails(this.scan[$scan._id], emails);
  }

  getRequiredSecurityForDownload(): IRequiredSecurity {
    return RequiredSecurities.AT_Mobile_Emulator_Scans_Download;
  }

  getRequiredSecurityForReportShare(): IRequiredSecurity {
    return RequiredSecurities.AT_Mobile_Emulator_Scans_Read;
  }
}

class MobileXmlScanReportHandler extends ScanReportHandler {
  constructor(
    private reportService: ReportService,
    private scan: IScan,
  ) {
    super();
  }

  generateFullScanReport(
    auditTool: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
    scanUrl: string,
  ): Observable<IScanReportResponse> {
    return this.reportService.generateFullScanReport(this.scan[$scan._id], fileExtension, scanUrl, auditTool);
  }

  getFullScanReport(
    auditTool: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
    scanUrl: string,
  ): Observable<IScanReportResponse> {
    return this.reportService.getFullScanReport(this.scan[$scan._id], fileExtension, scanUrl, auditTool);
  }

  sendReportToEmails(emails: string[], auditTool: AccessibilityAuditToolNames | null): Observable<string[]> {
    return this.reportService.sendReportToEmails(this.scan[$scan._id], auditTool, emails);
  }

  getRequiredSecurityForDownload(): IRequiredSecurity {
    return RequiredSecurities.AT_Mobile_Xml_Scans_Download;
  }

  getRequiredSecurityForReportShare(): IRequiredSecurity {
    return RequiredSecurities.AT_Mobile_Xml_Scans_Read;
  }
}

class MobileODTScanReportHandler extends ScanReportHandler {
  constructor(
    private reportService: ReportService,
    private scan: IScan,
  ) {
    super();
  }

  generateFullScanReport(
    auditTool: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
    scanUrl: string,
  ): Observable<IScanReportResponse> {
    return this.reportService.generateFullScanReport(this.scan[$scan._id], fileExtension, scanUrl, auditTool);
  }

  getFullScanReport(
    auditTool: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
    scanUrl: string,
  ): Observable<IScanReportResponse> {
    return this.reportService.getFullScanReport(this.scan[$scan._id], fileExtension, scanUrl, auditTool);
  }

  sendReportToEmails(emails: string[], auditTool: AccessibilityAuditToolNames | null): Observable<string[]> {
    return this.reportService.sendReportToEmails(this.scan[$scan._id], auditTool, emails);
  }

  getRequiredSecurityForDownload(): IRequiredSecurity {
    return RequiredSecurities.AT_Mobile_ODT_Scans_Download;
  }

  getRequiredSecurityForReportShare(): IRequiredSecurity {
    return RequiredSecurities.AT_Mobile_ODT_Scans_Read;
  }
}

class DefaultScanReportHandler extends ScanReportHandler {
  constructor(
    private reportService: ReportService,
    private scan: IScan,
  ) {
    super();
  }

  generateFullScanReport(
    auditTool: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
    scanUrl: string,
  ): Observable<IScanReportResponse> {
    return this.reportService.generateFullScanReport(this.scan[$scan._id], fileExtension, scanUrl, auditTool);
  }

  getFullScanReport(
    auditTool: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
    scanUrl: string,
  ): Observable<IScanReportResponse> {
    return this.reportService.getFullScanReport(this.scan[$scan._id], fileExtension, scanUrl, auditTool);
  }

  sendReportToEmails(emails: string[], auditTool: AccessibilityAuditToolNames | null): Observable<string[]> {
    return this.reportService.sendReportToEmails(this.scan[$scan._id], auditTool, emails);
  }

  getRequiredSecurityForDownload(): IRequiredSecurity {
    return RequiredSecurities.AT_Scans_Download;
  }

  getRequiredSecurityForReportShare(): IRequiredSecurity {
    return RequiredSecurities.AT_Scans_Read;
  }
}
